import { useCallback, useEffect, useState } from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  CommunicationOption,
  ContentProps,
} from 'models/profile-settings.model';
import { formatCommunicationOptions } from 'utils/communications/format-communication-options';
import { updateCommunicationOptions } from 'utils/communications/update-communication-options';
import {
  sendAccountSetupConfirmPaperlessScreenLoadedEvent,
  sendAccountSetupUnsubscribeScreenLoadedEvent,
  sendSettingsConfirmPaperlessScreenLoadedEvent,
  sendSettingsUnsubscribeScreenLoadedEvent,
} from 'utils/analytics-events';
import { useCommunicationModals } from './use-communication-modals.hook';

export const useCommunicationPreference = (
  initialOptions: CommunicationOption[],
  isAccountSetup?: boolean,
) => {
  const { formatMessage } = useIntl();
  const [options, setOptions] = useState<CommunicationOption[]>(initialOptions);
  const [pendingOptions, setPendingOptions] = useState<
    CommunicationOption[] | null
  >(null);
  const [contents, setContents] = useState<ContentProps[]>([]);
  const [hasUserChanges, setHasUserChanges] = useState(false);
  const [preEditOptions, setPreEditOptions] =
    useState<CommunicationOption[]>(initialOptions);

  const modals = useCommunicationModals(
    options,
    setOptions,
    setContents,
    (opts) => formatCommunicationOptions(opts, formatMessage),
    preEditOptions,
    setPreEditOptions,
    setPendingOptions,
    isAccountSetup,
  );

  useEffect(() => {
    // Initialize or update state only if initialOptions changes and no user changes exist
    if (initialOptions.length > 0 && !hasUserChanges) {
      setOptions(initialOptions);
      setPreEditOptions(initialOptions);
      setContents(formatCommunicationOptions(initialOptions, formatMessage));
    }
  }, [initialOptions, hasUserChanges, formatMessage]);

  const handleSave = useCallback(
    (editValues: Record<string, string | boolean>) => {
      const updatedOptions = options.map((option) => ({
        ...option,
        value: editValues[option.labelId] ?? option.value,
      }));
      setOptions(updatedOptions);
      // Don't update preEditOptions here - it should always represent the true initial state
      setContents(formatCommunicationOptions(updatedOptions, formatMessage));
      modals.setShowPaperlessModal(false);
      modals.setShowUnsubscribeModal(false);
      modals.setPendingPaperless(null);
      modals.setPendingUnsubscribe(null);
      setPendingOptions(null);
      setHasUserChanges(false);
    },
    [options, formatMessage, modals],
  );

  const handleCancel = useCallback(() => {
    setOptions(preEditOptions);
    setContents(formatCommunicationOptions(preEditOptions, formatMessage));
    setPendingOptions(null);
    modals.setShowPaperlessModal(false);
    modals.setShowUnsubscribeModal(false);
    modals.setPendingPaperless(null);
    modals.setPendingUnsubscribe(null);
    setHasUserChanges(false);
  }, [preEditOptions, modals, formatMessage]);

  const handleInputChange = useCallback(
    (id: string, value: boolean) => {
      setHasUserChanges(true);
      if (id === 'STR_PAPERLESS' && value) {
        modals.setPendingPaperless(true);
        modals.setShowPaperlessModal(true);
        if (isAccountSetup) {
          sendAccountSetupConfirmPaperlessScreenLoadedEvent();
        } else {
          sendSettingsConfirmPaperlessScreenLoadedEvent();
        }
      } else if (id === 'STR_PERSONALIZED_EMAIL' && !value) {
        const paperlessOption = options.find(
          (opt) => opt.labelId === 'STR_PAPERLESS',
        );
        const hasPaperless = !!paperlessOption;
        const isPaperlessYes = paperlessOption?.value === true;

        if (hasPaperless && isPaperlessYes) {
          modals.setPendingUnsubscribe(false);
          modals.setShowUnsubscribeModal(true);
          if (isAccountSetup) {
            sendAccountSetupUnsubscribeScreenLoadedEvent();
          } else {
            sendSettingsUnsubscribeScreenLoadedEvent();
          }
        } else {
          const updatedOptions = updateCommunicationOptions(options, id, value);
          setOptions(updatedOptions);
          setContents(
            formatCommunicationOptions(updatedOptions, formatMessage),
          );
          setPendingOptions(null);
        }
      } else {
        const updatedOptions = updateCommunicationOptions(options, id, value);
        setOptions(updatedOptions);
        setContents(formatCommunicationOptions(updatedOptions, formatMessage));
        setPendingOptions(null);
      }
    },
    [options, modals, formatMessage, isAccountSetup],
  );

  return {
    contents,
    handleSave,
    handleCancel,
    handleInputChange,
    currentOptions: pendingOptions || options,
    hasUserChanges,
    ...modals,
  };
};
